'use client';

import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { ViewModeToggle } from '../../atoms/ViewModeToggle';
import type { AccountListTabsValue } from '../../molecules/AccountListTabs/AccountListTabs.types';
import { AccountListTabsBar } from '../../molecules/AccountListTabsBar';
import { ProductBreadcrumbs } from '../../molecules/ProductBreadcrumbs';
import { UserAssetsCollection } from '../../molecules/UserAssetsList/UserAssetsCollection';
import { ProductListHeader } from '../../organisms/ProductListHeader';
import { ProductListInfo } from '../../organisms/ProductListInfo';
import { ProductListing } from '../../organisms/ProductListing';
import type {
  AccountListTemplateProps,
  AccountListTemplatePropsListingData,
} from './AccountListTemplate.types';

export const AccountListTemplate = ({
  className = '',
  title,
  listings,
  microcopy,
  favoriteListModalProps,
  breadcrumbs,
  organization,
  viewMode,
  onViewModeChange,
}: AccountListTemplateProps) => {
  const [activeTab, setActiveTab] = useState<AccountListTabsValue>('personal');

  const hasOrganization = !!organization;

  const organizationListings: AccountListTemplatePropsListingData[] =
    hasOrganization ? organization.organizationListings : [];

  const personalCount = listings.length;
  const organizationCount = organizationListings.length;

  const personalSectionId = 'personal-lists';
  const organizationSectionId = 'organization-lists';

  const renderCollectionView = (
    listingsToRender: AccountListTemplatePropsListingData[]
  ) => (
    <div className="flex flex-col gap-12 md:gap-6 lg:gap-12">
      {listingsToRender.map((listing) => (
        <ProductListing
          key={listing.href}
          name={listing.name}
          href={listing.href}
          info={listing.info}
          isEmpty={listing.isEmpty}
          microcopy={microcopy.listing}
          hideUserAvatar
        >
          {listing.children}
        </ProductListing>
      ))}
    </div>
  );

  const renderGridView = (
    listingsToRender: AccountListTemplatePropsListingData[]
  ) => (
    <div className="-mx-2 flex flex-wrap gap-y-12 md:gap-y-6">
      {listingsToRender.map((listing) => (
        <UserAssetsCollection
          key={listing.href}
          teaserImages={listing.teaserImages}
          link={listing.href}
          title={listing.name}
          length={listing.info.count}
          microcopy={microcopy.collection}
          customFooter={
            <div className="mt-4">
              <h3 className="break-words text-lg font-bold text-content md:text-3xl">
                {listing.name}
              </h3>
              <ProductListInfo
                count={listing.info.count}
                visibility={listing.info.visibility}
                microcopy={microcopy.collectionInfo}
              />
            </div>
          }
          className="mb-0 md:mb-0 lg:mb-0"
          isEmpty={listing.isEmpty}
        />
      ))}
    </div>
  );

  const renderListings = (
    listingsToRender: AccountListTemplatePropsListingData[]
  ) => {
    return viewMode === 'grid'
      ? renderGridView(listingsToRender)
      : renderCollectionView(listingsToRender);
  };

  return (
    <div
      className={c(
        'lg-container flex min-h-[25rem] flex-col gap-10 pb-10 pt-4 lg:gap-12 lg:pb-16',
        className
      )}
      data-test="account-list-template"
    >
      <ProductBreadcrumbs path={breadcrumbs.path} />
      <ProductListHeader
        type="new"
        size="xl"
        name={title}
        microcopy={microcopy.header}
        favoriteListModalProps={favoriteListModalProps}
        viewModeToggle={
          <ViewModeToggle
            value={viewMode}
            microcopy={microcopy.viewModeToggle}
            onValueChange={onViewModeChange}
          />
        }
      />
      <div>
        <AccountListTabsBar
          organization={
            organization && {
              ...organization,
              personalCount,
              organizationCount,
              activeTab,
              onTabChange: setActiveTab,
              personalSectionId,
              organizationSectionId,
            }
          }
          viewMode={viewMode}
          microcopy={microcopy.tabsBar}
          onViewModeChange={onViewModeChange}
          className="mb-5 lg:mb-10"
        />
        <section id={personalSectionId} aria-label="Personal lists">
          {renderListings(listings)}
        </section>
        {hasOrganization && (
          <section
            id={organizationSectionId}
            aria-label="Organization lists"
            className="mt-12 scroll-smooth md:mt-6 lg:mt-16"
          >
            {renderListings(organizationListings)}
          </section>
        )}
      </div>
    </div>
  );
};

AccountListTemplate.displayName = 'AccountListTemplate';
